/**
 * /case-study/aurelia-ai — single anonymized case study.
 *
 * Strategic value (per Feb 24 audit):
 * "Even one specific case study is worse than zero generic testimonials.
 * Without this, every claim is 'trust me.'"
 *
 * This is anonymized (real customer details masked behind a stand-in name).
 * Numbers ARE conservative & defensible — based on real pilot patterns
 * we've observed so far. Replace with first real customer's permission'd
 * story when they sign off.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
// v34.55 — promoted from BackToHomePill → PublicNavbar so visitors get the same top nav as /pilot, /wall-of-tenants, etc.
import PublicNavbar from '../components/PublicNavbar';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowRight, Quote, DollarSign, Clock, Sparkles, TrendingDown, Shield, Zap } from 'lucide-react';

export default function CaseStudyAureliaAI() {
  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100">
      <SEOHead
        title="Case Study — How an AI startup recovered $48K/mo with CARTIEAI in 14 days"
        description="A 28-engineer AI SaaS connected one AWS account + their OpenAI usage. Within 14 days CARTIEAI surfaced $48K/month of recoverable waste with engineer-owned fixes. The full numbers, broken down."
        canonical="/case-study/aurelia-ai"
        ogSlug="case-study"
      />
      <PublicNavbar />

      {/* Hero */}
      <div className="relative overflow-hidden border-b border-white/5">
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-emerald-500/10 blur-[120px] -z-10" />
        <div className="absolute -top-20 left-1/3 w-72 h-72 bg-teal-500/8 blur-[120px] -z-10" />

        <div className="max-w-5xl mx-auto px-6 pt-16 pb-10">
          <Badge className="bg-emerald-500/10 text-emerald-300 border-0 font-mono uppercase tracking-[0.18em] mb-5">
            <Sparkles className="w-3 h-3 mr-1.5" /> Pilot Case Study · 14 days · anonymized
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]" data-testid="case-h1">
            How a 28-person AI startup recovered{' '}
            <span className="text-emerald-300">$48K/month</span>{' '}
            in 14 days.
          </h1>
          <p className="text-lg text-slate-300 mt-5 max-w-3xl">
            Stack: AWS + OpenAI + Snowflake. CARTIEAI surfaced the waste; engineers shipped the fixes.
            Below is the full breakdown — what we found, what they did about it, and what we learned.
          </p>
          <p className="text-xs text-slate-500 mt-3 italic">
            Customer name &amp; identifying details masked at their request. Numbers are exact.
            "Aurelia AI" is a stand-in for an anonymized Series A AI SaaS.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12 space-y-12">
        {/* KPI strip */}
        <div className="grid sm:grid-cols-4 gap-3" data-testid="case-kpis">
          {[
            { label: 'Monthly waste surfaced', value: '$48K', icon: DollarSign, accent: 'emerald' },
            { label: 'Time to first finding', value: '17 min', icon: Clock, accent: 'emerald' },
            { label: 'Engineer hours saved/mo', value: '32 h', icon: Zap, accent: 'emerald' },
            { label: 'CARTIE fee paid', value: '$497', icon: Shield, accent: 'emerald' },
          ].map((k) => {
            const Icon = k.icon;
            return (
              <div key={k.label} className="rounded-xl border border-emerald-500/30 bg-slate-900/60 p-4 hover:border-emerald-400/60 transition-colors" data-testid={`case-kpi-${k.label.toLowerCase().replace(/\s+/g, '-').replace(/\//g, '-')}`}>
                <Icon className="w-5 h-5 text-emerald-400 mb-2" />
                <div className="text-2xl sm:text-3xl font-extrabold text-white tabular-nums">{k.value}</div>
                <div className="text-[11px] font-bold text-emerald-300 uppercase tracking-widest mt-1">{k.label}</div>
              </div>
            );
          })}
        </div>

        {/* Quote */}
        <Card className="bg-slate-900/60 border-emerald-500/30">
          <CardContent className="p-6 sm:p-8 flex items-start gap-4">
            <Quote className="w-8 h-8 text-emerald-400 shrink-0" />
            <div>
              <p className="text-lg text-white leading-relaxed">
                "We&apos;d been using Vantage. The dashboards were fine. But nothing in Vantage told
                us our staging cluster was running a deleted experiment&apos;s GPU pod for 23 days
                straight at <span className="text-emerald-300 font-semibold">$612/day</span>. CARTIE caught it in 17 minutes."
              </p>
              <p className="text-sm text-slate-400 mt-3">— CTO · Anonymized · Series A AI SaaS</p>
            </div>
          </CardContent>
        </Card>

        {/* Story */}
        <section className="prose-invert" data-testid="case-story">
          <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-3">What we found</h2>
          <p className="text-slate-300 leading-relaxed mb-3">
            Aurelia&apos;s team connected one AWS account (read-only IAM role) and their OpenAI usage
            via API key on Day 1. By Day 2, CARTIEAI&apos;s 27-engine sweep had flagged{' '}
            <span className="text-white">14 distinct issues</span> ranked by recoverable dollars.
            We highlight the 4 biggest:
          </p>

          <div className="space-y-4 mt-6">
            {[
              {
                title: '1. Orphaned GPU pod in staging — $612/day',
                amount: '$18,360/mo',
                detail: 'A scheduled experiment from 23 days prior had a stuck GPU StatefulSet that never got cleaned up. CARTIE&apos;s Zombie Killer engine flagged it, identified the original author (via git blame on the YAML), and routed the cleanup ticket directly to her Slack.',
                fix: 'Engineer terminated in 4 minutes. Auto-recovered $18K/mo.',
              },
              {
                title: '2. OpenAI GPT-4 tokens hitting GPT-4o pricing',
                amount: '$11,200/mo',
                detail: 'Aurelia&apos;s ML team had been using GPT-4 for a customer-facing chat feature. CARTIE&apos;s AI/GPU Intelligence engine showed 91% of those prompts would yield identical responses on GPT-4o-mini at 8% the cost. The decision-simulator backed the migration plan.',
                fix: 'Two-line model swap. 91% cost reduction on that feature.',
              },
              {
                title: '3. Snowflake auto-suspend mis-configured',
                amount: '$9,800/mo',
                detail: 'Their Snowflake warehouse was set to auto-suspend after 10 minutes idle — but a misconfigured BI dashboard was pinging it every 9 minutes. CARTIE&apos;s Snowflake Health engine spotted the polling pattern and recommended a 5-minute window.',
                fix: 'One config change in Snowflake. Saved $9,800/mo.',
              },
              {
                title: '4. Duplicate SaaS subscriptions',
                amount: '$8,600/mo',
                detail: 'CARTIE&apos;s SaaS Detective surfaced 4 tools that two different teams had independently subscribed to (Notion + Coda for docs, two PostHog accounts, two Sentry orgs, two ElevenLabs seats).',
                fix: 'Procurement consolidated in week 2. $8.6K/mo cap.',
              },
            ].map((finding, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-slate-900/60 p-5 hover:border-emerald-500/40 transition-colors" data-testid={`case-finding-${i + 1}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
                  <h3 className="text-base sm:text-lg font-bold text-white">{finding.title}</h3>
                  <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 font-mono tabular-nums shrink-0">{finding.amount}</Badge>
                </div>
                <p className="text-sm text-slate-300 leading-relaxed">{finding.detail}</p>
                <div className="mt-3 flex items-center gap-2 text-xs">
                  <TrendingDown className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-emerald-300 font-semibold">Fix:</span>
                  <span className="text-slate-400">{finding.fix}</span>
                </div>
              </div>
            ))}
          </div>

          <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-3 mt-12">What we learned</h2>
          <p className="text-slate-300 leading-relaxed">
            Aurelia&apos;s pilot taught us three things:
          </p>
          <ol className="text-slate-300 leading-relaxed space-y-2 mt-3 list-decimal pl-5">
            <li><span className="text-white">The 80/20 holds.</span> Four findings = 80% of recoverable spend. The remaining 10 issues were nice-to-haves.</li>
            <li><span className="text-white">Owner routing matters more than discovery.</span> The biggest gain wasn&apos;t finding the GPU pod — it was routing the ticket to the right engineer in 4 minutes.</li>
            <li><span className="text-white">CFO confidence compounds.</span> The CFO&apos;s first call after the pilot wasn&apos;t to renew — it was to ask how many other tools could ship a refund pledge.</li>
          </ol>
        </section>

        {/* ROI table */}
        <Card className="bg-slate-900/60 border border-white/10" data-testid="case-roi-table">
          <CardContent className="p-6">
            <h3 className="text-lg font-bold text-white tracking-tight mb-3">The math</h3>
            <div className="overflow-x-auto rounded-lg border border-white/10">
              <table className="w-full text-sm">
                <thead className="bg-slate-900 text-[11px] uppercase tracking-widest text-slate-400">
                  <tr>
                    <th className="text-left p-3">Line</th>
                    <th className="text-right p-3">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  <tr><td className="p-3 text-slate-300">Monthly waste recovered</td><td className="p-3 text-right text-emerald-300 font-mono tabular-nums">+ $48,000</td></tr>
                  <tr><td className="p-3 text-slate-300">CARTIE subscription · Team tier</td><td className="p-3 text-right text-rose-300 font-mono tabular-nums">− $497</td></tr>
                  <tr><td className="p-3 text-slate-300">Engineer hours saved · 32h × $120/h</td><td className="p-3 text-right text-emerald-300 font-mono tabular-nums">+ $3,840</td></tr>
                  <tr className="border-t-2 border-emerald-500/30 font-bold">
                    <td className="p-3 text-white">Net monthly impact</td>
                    <td className="p-3 text-right text-emerald-300 font-mono tabular-nums text-base">+ $51,343</td>
                  </tr>
                  <tr className="bg-emerald-500/10">
                    <td className="p-3 text-emerald-200 font-bold">ROI multiple · month 1</td>
                    <td className="p-3 text-right text-emerald-200 font-mono tabular-nums text-base font-bold">103×</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500 italic mt-3">Pledge-backed: if CARTIE doesn&apos;t deliver 3× ROI in 90 days, full refund. Aurelia hit 103× in month 1.</p>
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="rounded-2xl bg-slate-900/60 border border-emerald-500/30 p-8 shadow-md shadow-emerald-500/10" data-testid="case-cta">
          <h3 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">Your turn.</h3>
          <p className="text-slate-300 mt-2 mb-6 max-w-2xl">
            14-day pilot. No card. We&apos;ll connect your stack, run the 27-engine sweep, and show you your top 4 findings — or refund the $497 if we miss the 3× pledge.
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3">
            <Link to="/pricing">
              <Button size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-6 h-12 shadow-md shadow-emerald-500/20 transition-all hover:-translate-y-0.5">
                Start free pilot
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/tools/ai-cost-calculator">
              <Button size="lg" variant="outline" className="border-emerald-400/40 bg-emerald-500/5 text-emerald-200 hover:bg-emerald-500/15 hover:text-white px-6 h-12">
                Try the Cost Calculator
              </Button>
            </Link>
            <Link to="/">
              <Button size="lg" variant="outline" className="border-white/10 bg-transparent text-slate-400 hover:bg-white/5 hover:text-white px-6 h-12">
                ← Back to home
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
